import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  X,
  ArrowRight,
  MessageSquare,
  PenLine,
  TrendingUp,
  Brain,
  Shield,
  Target,
  Sparkles,
  Moon,
  Zap
} from 'lucide-react';

const comparisonData = [
  { feature: 'AI Life Coach', vzmo: true, calm: false },
  { feature: 'Personalized Guidance', vzmo: true, calm: false },
  { feature: 'Remembers Your Context', vzmo: true, calm: false },
  { feature: 'Private Journaling', vzmo: true, calm: false },
  { feature: 'Action Plan Creation', vzmo: true, calm: false },
  { feature: 'Weekly Progress Tracking', vzmo: true, calm: true },
  { feature: 'Career Coaching', vzmo: true, calm: false },
  { feature: 'Relationship Guidance', vzmo: true, calm: false },
  { feature: 'Sleep Stories', vzmo: false, calm: true },
  { feature: 'Guided Meditation', vzmo: false, calm: true },
  { feature: 'Breathing Exercises', vzmo: false, calm: true },
  { feature: '24/7 Availability', vzmo: true, calm: true },
];

const testimonials = [
  {
    quote: "Calm helped me sleep better, but I still woke up anxious about work. VZMO actually helps me figure out what to do about it.",
    author: "David L.",
    role: "Product Manager",
    switched: "Former Calm subscriber"
  },
  {
    quote: "I loved the sleep stories but needed more than relaxation. Now I have a coach who knows my whole situation.",
    author: "Rachel P.",
    role: "Entrepreneur",
    switched: "Switched from Calm Premium"
  },
  {
    quote: "After 3 years of Calm, I realized I was just managing stress, not solving problems. VZMO changed that.",
    author: "Alex W.",
    role: "Healthcare Professional",
    switched: "Long-time Calm user"
  }
];

const switchReasons = [
  {
    icon: Brain,
    title: "Beyond Relaxation",
    description: "Calm helps you unwind. VZMO helps you understand why you're wound up in the first place - and what to do about it."
  },
  {
    icon: MessageSquare,
    title: "A Coach Who Listens",
    description: "Not pre-recorded content. A personalized AI that remembers your story, understands your goals, and gives advice that fits your life."
  },
  {
    icon: Target,
    title: "From Insight to Action",
    description: "Clarity without action is just wishful thinking. Get specific plans and weekly accountability to actually change your life."
  },
  {
    icon: Shield,
    title: "Complete Privacy",
    description: "Your thoughts stay yours. No social features, no data selling. Just a private space for honest self-reflection."
  }
];

export function CalmAlternativePage() {
  useEffect(() => {
    document.title = 'Calm Alternative for Life Coaching | VZMO';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Looking for a Calm alternative that offers more than sleep stories? VZMO provides AI life coaching, personalized guidance, and real action plans. Try free for 7 days.');
    }
  }, []);

  return (
    <div className="bg-white pt-20">
      <section className="py-16 sm:py-24 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="inline-block text-coral-400 font-medium text-sm tracking-wide uppercase mb-4">
              Calm Alternative
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
              Sleep better. Then
              <span className="block text-coral-400">live better.</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed mb-8">
              Calm is great for winding down. But what about the problems keeping you up at night?
              VZMO helps you actually solve them.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center px-8 py-4 bg-coral-500 text-white rounded-full font-semibold text-lg hover:bg-coral-600 transition-all shadow-lg"
              >
                Try VZMO free for 7 days
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <a
                href="#comparison"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all"
              >
                See the difference
              </a>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-16">
            {[
              { icon: PenLine, label: 'Journal', desc: 'Process your thoughts' },
              { icon: MessageSquare, label: 'Coach', desc: 'Get real advice' },
              { icon: TrendingUp, label: 'Track', desc: 'See your progress' }
            ].map((item, index) => (
              <div key={index} className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10 text-center">
                <div className="w-12 h-12 rounded-xl bg-coral-500/20 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-6 w-6 text-coral-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">{item.label}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Why people switch from Calm
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Relaxation is the starting point. Real change requires action.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {switchReasons.map((reason, index) => (
              <div key={index} className="bg-[#FDFBF7] rounded-2xl p-8 border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-coral-100 flex items-center justify-center mb-5">
                  <reason.icon className="h-6 w-6 text-coral-600" />
                </div>
                <h3 className="text-xl font-heading font-bold text-slate-900 mb-3">
                  {reason.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {reason.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <Moon className="h-12 w-12 text-coral-500 mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              The sleep story paradox
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              You drift off peacefully. But then you wake up and the same worries are waiting for you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <Moon className="h-6 w-6 text-slate-400" />
                <h3 className="font-heading font-bold text-slate-900">Calm approach</h3>
              </div>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-1">-</span>
                  <span>Listen to sleep story</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-1">-</span>
                  <span>Fall asleep faster</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-1">-</span>
                  <span>Wake up with same problems</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-1">-</span>
                  <span>Repeat tomorrow</span>
                </li>
              </ul>
            </div>
            <div className="bg-slate-900 rounded-2xl p-8 border border-slate-700">
              <div className="flex items-center gap-3 mb-4">
                <Zap className="h-6 w-6 text-coral-400" />
                <h3 className="font-heading font-bold text-white">VZMO approach</h3>
              </div>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-coral-400 mt-0.5 flex-shrink-0" />
                  <span>Identify what's keeping you up</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-coral-400 mt-0.5 flex-shrink-0" />
                  <span>Get a plan to address it</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-coral-400 mt-0.5 flex-shrink-0" />
                  <span>Take action during the day</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-coral-400 mt-0.5 flex-shrink-0" />
                  <span>Sleep better because problems are solved</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="comparison" className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Feature comparison
            </h2>
            <p className="text-lg text-slate-600">
              See how VZMO compares to Calm
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-4 px-6 font-semibold text-slate-900">Feature</th>
                    <th className="text-center py-4 px-4 font-semibold text-coral-600">VZMO</th>
                    <th className="text-center py-4 px-4 font-semibold text-slate-500">Calm</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <td className="py-4 px-6 text-slate-700">{row.feature}</td>
                      <td className="py-4 px-4 text-center">
                        {row.vzmo ? (
                          <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center mx-auto">
                            <Check className="h-4 w-4 text-sage-600" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
                            <X className="h-4 w-4 text-slate-400" />
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {row.calm ? (
                          <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center mx-auto">
                            <Check className="h-4 w-4 text-sage-600" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
                            <X className="h-4 w-4 text-slate-400" />
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-center text-sm text-slate-500 mt-6">
            Comparison based on publicly available features as of 2024.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              What switchers say
            </h2>
            <p className="text-lg text-slate-600">
              Real feedback from people who wanted more than relaxation
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-[#FDFBF7] rounded-2xl p-8 border border-slate-100">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Sparkles key={i} className="h-4 w-4 text-coral-400" />
                  ))}
                </div>
                <blockquote className="text-slate-700 leading-relaxed mb-6">
                  "{testimonial.quote}"
                </blockquote>
                <div>
                  <p className="font-semibold text-slate-900">{testimonial.author}</p>
                  <p className="text-sm text-slate-500">{testimonial.role}</p>
                  <p className="text-xs text-coral-600 mt-1">{testimonial.switched}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Common questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: "I love Calm's sleep stories. Can I still use them?",
                a: "Absolutely! Many users keep Calm for sleep content and use VZMO during the day for coaching. They serve different purposes and work great together."
              },
              {
                q: "How is this different from the Daily Calm?",
                a: "The Daily Calm is a pre-recorded meditation session. VZMO is an interactive AI coach that knows your specific situation and gives personalized advice. It's the difference between listening to a podcast and having a real conversation."
              },
              {
                q: "Will VZMO help with my anxiety like Calm does?",
                a: "VZMO addresses anxiety differently - by helping you identify and solve the problems causing it. Many users find that taking action on their worries is more effective than just managing the symptoms."
              },
              {
                q: "I've been a Calm subscriber for years. Is it worth switching?",
                a: "If you feel like you've plateaued or want more than relaxation, try VZMO's free trial alongside Calm. Many long-term Calm users discover they were ready for something more action-oriented."
              },
              {
                q: "What about Calm's masterclasses?",
                a: "Calm's masterclasses are excellent educational content. VZMO is different - it's about applying insights to your specific life. Instead of learning about goal-setting in general, you'll create actual goals with accountability."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
            Ready for more than relaxation?
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            7-day free trial. No credit card required. Keep using Calm too.
          </p>

          <Link
            to="/signup"
            className="inline-flex items-center px-10 py-4 bg-coral-500 text-white rounded-full font-semibold text-lg hover:bg-coral-600 transition-all shadow-lg"
          >
            Start your free trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>

          <p className="text-slate-400 text-sm mt-6">
            Join thousands who upgraded from meditation to coaching
          </p>
        </div>
      </section>
    </div>
  );
}
